import React from 'react';
import toast, { CheckmarkIcon, ErrorIcon } from 'react-hot-toast';
import { ToastConstants } from '@constants/toastConstants';

interface CustomErrorToastOptions {
  message: React.ReactNode;
  id?: string;
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

// Track active custom toast ids so we can enforce a max stack and dismiss bottom-most
let activeCustomToastIds: string[] = [];
const removeActiveId = (id: string) => {
  activeCustomToastIds = activeCustomToastIds.filter(x => x !== id);
};

export const showCustomErrorToast = ({
  message,
  id,
  duration = ToastConstants.errorDurationMs,
  position = ToastConstants.positionTopCenter,
}: CustomErrorToastOptions) => {
  const createdId = toast.custom(
    (t: any) => (
      <div
        role="status"
        aria-live="polite"
        style={{
          transition: ToastConstants.transition,
          transform: t.visible ? ToastConstants.transformVisible : ToastConstants.transformHidden,
          opacity: t.visible ? ToastConstants.opacityVisible : ToastConstants.opacityHidden,
          minWidth: ToastConstants.errorMinWidth,
          padding: ToastConstants.errorPadding,
          boxShadow: ToastConstants.errorBoxShadow,
          fontWeight: ToastConstants.fontWeight,
          backgroundColor: ToastConstants.backgroundColor,
          color: ToastConstants.textColor,
          borderRadius: ToastConstants.borderRadius,
          display: ToastConstants.display,
          alignItems: ToastConstants.alignItems,
          gap: ToastConstants.iconGap,
        }}
      >
        <ErrorIcon />
        <div style={{ flex: 1 }}>
          {message}
        </div>
      </div>
    ),
    {
      id,
      duration,
      position,
    }
  );
  
  // Keep our internal list in sync when a toast fully closes (fallback via timeout)
  // Note: react-hot-toast doesn't expose onClose in options type, so we emulate via duration
  setTimeout(() => removeActiveId(createdId), duration + 50);
  
  // Track newest at the top
  activeCustomToastIds.unshift(createdId);
  
  // Enforce max stack size: if more than allowed, dismiss bottom-most immediately
  if (activeCustomToastIds.length > ToastConstants.maxCustomToasts) {
    const bottomId = activeCustomToastIds.pop();
    if (bottomId) toast.dismiss(bottomId);
  }
};

interface CustomSuccessToastOptions {
  message: React.ReactNode;
  id?: string;
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  icon?: React.ReactNode;
}

export const showCustomSuccessToast = ({
  message,
  id,
  duration = ToastConstants.successDurationMs,
  position = ToastConstants.positionTopCenter,
  minWidth = ToastConstants.successMinWidth,
  maxWidth,
  width,
  icon,
}: CustomSuccessToastOptions) => {
  const createdId = toast.custom(
    (t: any) => (
      <div
        role="status"
        aria-live="polite"
        style={{
          transition: ToastConstants.transition,
          transform: t.visible ? ToastConstants.transformVisible : ToastConstants.transformHidden,
          opacity: t.visible ? ToastConstants.opacityVisible : ToastConstants.opacityHidden,
          minWidth,
          maxWidth,
          width,
          padding: ToastConstants.successPadding,
          boxShadow: ToastConstants.successBoxShadow,
          fontWeight: ToastConstants.fontWeight,
          backgroundColor: ToastConstants.backgroundColor,
          color: ToastConstants.textColor,
          borderRadius: ToastConstants.borderRadius,
          display: ToastConstants.display,
          alignItems: ToastConstants.alignItems,
          gap: ToastConstants.iconGap,
        }}
      >
        {icon ?? <CheckmarkIcon />}
        <div style={{ flex: 1 }}>
          {message}
        </div>
      </div>
    ),
    {
      id,
      duration,
      position,
    }
  );

  // Track newest at the top
  activeCustomToastIds.unshift(createdId);

  // Enforce max stack size across all custom toasts
  if (activeCustomToastIds.length > ToastConstants.maxCustomToasts) {
    const bottomId = activeCustomToastIds.pop();
    if (bottomId) toast.dismiss(bottomId);
  }

  // Remove id after close via timeout fallback
  setTimeout(() => removeActiveId(createdId), duration + 50);
};

